import { LazyMotion, domAnimation } from "framer-motion";
import { useCallback, useMemo, useState } from "react";
import {
  AuthCallbackPage,
  isKindeCallbackUrl,
  readAuthSession,
  type AuthSession,
} from "@src/auth/AuthCallbackPage";
import { ClerkAuthShell } from "@src/auth/ClerkAuthShell";
import { ClerkSsoCallbackPage, isClerkSsoCallbackUrl } from "@src/auth/ClerkSsoCallbackPage";
import { FunnelAuthShell } from "@src/auth/FunnelAuthShell";
import { isSsoRedirectUrl, SsoRedirectPage } from "@src/auth/SsoRedirectPage";
import { Funnel } from "@src/funnel/Funnel";
import { FunnelLayoutContext } from "@src/funnel/FunnelLayoutContext";
import { ApplicationContextProvider } from "@src/funnel/useApplicationContext";

/**
 * Kinde redirect detection: if the current URL carries `?code=&state=`, we
 * render the callback loader (still inside the auth shell so KindeProvider
 * can finish the PKCE exchange). `?sso=` triggers SSO redirect (social
 * login initiated from Kinde custom auth pages). Otherwise we render the
 * funnel as usual.
 */
const isCallbackRender = (): boolean => isKindeCallbackUrl(window.location.search);
const isSsoRender = (): boolean => isSsoRedirectUrl(window.location.search);
const isClerkSsoRender = (): boolean => isClerkSsoCallbackUrl(window.location.search);

export const App = () => {
  // Capture values once on mount via lazy initializers. Reading them on
  // every render makes `session` go stale: `AuthCallbackPage`'s effect calls
  // `clearAuthSession()` before `window.location.replace` navigates, and any
  // re-render in that gap (e.g. `ApplicationContextProvider` going
  // loading → ready) would null out `session.authConfig` and remount
  // `KindeProvider` mid-PKCE exchange under a different (or no) config.
  const [callback] = useState<boolean>(() => isCallbackRender());
  const [ssoRedirect] = useState<boolean>(() => isSsoRender());
  const [clerkSsoCallback] = useState<boolean>(() => isClerkSsoRender());
  const [session] = useState<AuthSession | null>(() =>
    callback || ssoRedirect ? readAuthSession() : null,
  );

  const [wideLayout, setWideLayoutRaw] = useState(false);
  const setWideLayout = useCallback((wide: boolean) => setWideLayoutRaw(wide), []);
  const layoutCtx = useMemo(() => ({ wideLayout, setWideLayout }), [wideLayout, setWideLayout]);

  const renderContent = () => {
    if (callback) return <AuthCallbackPage />;
    if (ssoRedirect) return <SsoRedirectPage />;
    if (clerkSsoCallback) return <ClerkSsoCallbackPage />;
    return <Funnel />;
  };

  return (
    <LazyMotion features={domAnimation} strict>
      <ApplicationContextProvider>
        <FunnelLayoutContext.Provider value={layoutCtx}>
          <div className="h-dvh w-full bg-[hsl(var(--brand-bg))]">
            <div
              className={`mx-auto flex h-full flex-col shadow-2xl shadow-black/30 ${
                wideLayout ? "max-w-3xl" : "max-w-md sm:max-w-md"
              }`}
            >
              <FunnelAuthShell configOverride={session?.authConfig}>
                <ClerkAuthShell>{renderContent()}</ClerkAuthShell>
              </FunnelAuthShell>
            </div>
          </div>
        </FunnelLayoutContext.Provider>
      </ApplicationContextProvider>
    </LazyMotion>
  );
};
